import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Download, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { accountingError } from '@/lib/accounting';
import { getAccountingInvoiceDocument } from '@/lib/api';

/**
 * Export one invoice as a PDF.
 *
 * Two steps rather than one, because the two are genuinely different: the file
 * has to be produced before it can be handed over, and producing it can fail.
 * A single button that silently does nothing for two seconds and then may or
 * may not have downloaded something is the version of this that generates
 * support tickets.
 *
 * The bytes come from the portal's existing invoice document endpoint — the
 * stored PDF for an application invoice, rendered on demand for one raised in
 * Accounting — so this is a download button, not a second renderer.
 */
export default function InvoicePdfDialog({ invoice, open, onOpenChange }) {
  const [status, setStatus] = useState('preparing');
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);
  const objectUrl = useRef(null);

  const cleanup = useCallback(() => {
    if (objectUrl.current) {
      URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  useEffect(() => {
    if (!open || !invoice?.ref) return undefined;
    let cancelled = false;
    setStatus('preparing');
    setError('');
    setFile(null);
    (async () => {
      try {
        const response = await getAccountingInvoiceDocument(invoice.ref);
        if (cancelled) return;
        const blob = response?.data;
        const fallbackName = `MCC_Invoice_${invoice.number}.pdf`;

        // The endpoint answers in one of two shapes: a stored file's short-lived
        // link, or the PDF bytes themselves. Both arrive as a Blob because the
        // request asked for one, so the JSON case is unwrapped here.
        if (blob?.type && blob.type.includes('application/json')) {
          const parsed = JSON.parse(await blob.text());
          if (!parsed?.url) throw new Error('no url');
          setFile({ href: parsed.url, name: parsed.file_name || fallbackName, remote: true });
        } else {
          cleanup();
          objectUrl.current = URL.createObjectURL(blob);
          setFile({ href: objectUrl.current, name: fallbackName, remote: false });
        }
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        let message = 'The invoice PDF could not be produced.';
        const body = err?.response?.data;
        if (body instanceof Blob) {
          try {
            const parsed = JSON.parse(await body.text());
            if (parsed?.detail) message = parsed.detail;
          } catch {
            /* keep the fallback */
          }
        } else {
          message = accountingError(err, message);
        }
        setError(message);
        setStatus('failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, invoice?.ref, invoice?.number, cleanup]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Export as PDF</DialogTitle>
          <DialogDescription>
            {invoice && <>Invoice {invoice.number} · {invoice.customer?.name}</>}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-3 rounded-lg border border-[rgba(6,43,26,0.1)] bg-[#F7F8FA] px-4 py-3.5 text-sm">
          {status === 'preparing' && (
            <>
              <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin text-[#D4AF37]" />
              <span className="text-[#062B1A]/70">Producing the invoice PDF…</span>
            </>
          )}
          {status === 'ready' && (
            <>
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
              <span className="min-w-0 text-[#062B1A]/80">
                Your invoice is ready to download.
                <span className="mt-0.5 block truncate font-mono text-[11px] text-[#062B1A]/50">
                  {file?.name}
                </span>
              </span>
            </>
          )}
          {status === 'failed' && (
            <>
              <FileText className="mt-0.5 size-4 shrink-0 text-red-600" />
              <span className="text-red-700">{error}</span>
            </>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            asChild={status === 'ready'}
            type="button"
            disabled={status !== 'ready'}
            className="bg-[#062B1A] text-white hover:bg-[#041B10]"
          >
            {status === 'ready' ? (
              <a
                href={file.href}
                download={file.name}
                target={file.remote ? '_blank' : undefined}
                rel={file.remote ? 'noopener noreferrer' : undefined}
                onClick={() => onOpenChange(false)}
              >
                <Download className="mr-2 size-4" />
                Download PDF
              </a>
            ) : (
              <span>
                <Download className="mr-2 size-4" />
                Download PDF
              </span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
